import { type FrontAuctionDTO } from '@capsicum/common';
import { usePartnerFiltered } from '@capsicum/socios-core';
import { useCallback, useMemo, useState } from 'react';

import { useRPAuctionsMeQueries, useRPAuctionsQueries } from '../data-access/queries/useRPAuctionsQueries';

import { useUserAuctionsUpdateFromWebSocket } from './components/useUserAuctionsUpdateFromWebSocket';
import { useProductTypes } from './useProductTypes';

export const useRPAuctions = ({
	forcedPartnerIds = [],
	isWebSocketDismissed = false
}: { forcedPartnerIds?: Array<string>; isWebSocketDismissed?: boolean } = {}) => {
	const { selectedPartners } = usePartnerFiltered();

	const { isLoading: isLoadingProductTypes, productTypes } = useProductTypes({
		...(forcedPartnerIds.length > 0 && { 'partnerIds[]': forcedPartnerIds }),
		'productRedeemTypes[]': ['auction']
	}); // filter wont be used in fanshop page for now so we send only forcedPartnerIds

	const [productTypesSelected, setProductTypesSelected] = useState<Array<string>>([]);
	const [search, setSearch] = useState<string>('');

	const params = useMemo(() => {
		// Priority: forcedPartnerIds (from props) > selectedPartners (from global filter)
		// If forcedPartnerIds are provided, ignore selectedPartners completely
		const effectivePartnerIds = forcedPartnerIds.length > 0 ? forcedPartnerIds : selectedPartners;

		return {
			...(productTypesSelected.length &&
				productTypesSelected[0] !== 'all' && { 'productTypeIds[]': productTypesSelected }),
			...(search.length && { 'productVariantNames[]': [search] }),
			...(effectivePartnerIds.length > 0 && {
				'partnerIds[]': effectivePartnerIds
			})
		};
	}, [productTypesSelected, search, selectedPartners, forcedPartnerIds]);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
		isLoading,
		refetch: refetchAuctions
	} = useRPAuctionsQueries(params, undefined, 8);

	const { data: userAuctionsData, refetch: refetchAuctionsMe } = useRPAuctionsMeQueries({
		'partnerIds[]': forcedPartnerIds // use only forcedPartnerIds for user auctions, for now we wont filter them by the selectedPartners filter only when forcing the partnerIds
	});

	const userAuctions: Array<FrontAuctionDTO> = useMemo(
		() =>
			(userAuctionsData &&
				userAuctionsData?.pages.flatMap(page => {
					return page?.data || [];
				})) ??
			[],
		[userAuctionsData]
	);

	const userAuctionIds = useMemo(() => {
		return userAuctions?.map(auction => auction.auctionId).filter((id): id is string => Boolean(id)) ?? [];
	}, [userAuctions]);

	const userAuctionProductIds = useMemo(() => {
		return userAuctions?.map(auction => auction.product?.id).filter((id): id is string => Boolean(id)) ?? [];
	}, [userAuctions]);

	useUserAuctionsUpdateFromWebSocket({
		isDismissed: isWebSocketDismissed,
		userAuctionIds,
		userAuctionProductIds
	});

	const auctions: Array<FrontAuctionDTO> = useMemo(
		() =>
			(data &&
				data?.pages.flatMap(page => {
					return page?.data || [];
				})) ??
			[],
		[data]
	);

	const handleSearch = useCallback((event: string) => {
		return setSearch(event);
	}, []);

	const chipsProductTypes = useMemo(() => {
		return productTypes?.map(category => {
			return { description: category.id || '', name: category.name || '', value: category.id || '' };
		});
	}, [productTypes]);

	const selectProductType = useCallback((selected: Array<string>) => {
		setProductTypesSelected(selected);
	}, []);

	return {
		auctions,
		chipsProductTypes,
		fetchNextPage,
		handleSearch,
		hasNextPage,
		isFetched,
		isFetching,
		isFetchingNextPage,
		isLoading,
		isLoadingProductTypes,
		refetchAuctions,
		refetchAuctionsMe,
		selectedPartners,
		selectProductType,
		userAuctions
	};
};
